﻿import clsx from 'clsx';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { hasRunwayUsage, pickRunwayAtBearing, runwayBarScale } from './analysis';
import { SOURCE_LABELS, useCrosswind } from './context';
import { SegmentedControl } from './SegmentedControl';
import { Tabs } from './Tabs';
import type { RunwayUsage } from './types';

export function CompassSection() {
    const {
        canvasRef,
        magneticVariation,
        headingMode,
        showMagVarIndicator,
        runwayGhosts,
        setHoveredRunwayId,
        selectRunway
    } = useCrosswind();

    const bearingAt = (e: React.MouseEvent<HTMLCanvasElement>): number | null => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;
        if (Math.hypot(x, y) < 12) {
            return null;
        }
        return ((Math.atan2(x, -y) * 180) / Math.PI + 360) % 360;
    };

    const handleMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
        const bearing = bearingAt(e);
        const row = bearing == null ? null : pickRunwayAtBearing(runwayGhosts, bearing);
        e.currentTarget.style.cursor = row ? 'pointer' : '';
        setHoveredRunwayId((prev) => (prev === (row?.id ?? null) ? prev : row?.id ?? null));
    };

    const handleClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
        const bearing = bearingAt(e);
        const row = bearing == null ? null : pickRunwayAtBearing(runwayGhosts, bearing);
        if (row) {
            selectRunway(row);
        }
    };

    const staticRadius = 30;
    const otherNorthDeg = headingMode === 'magnetic' ? -staticRadius : staticRadius;
    const modeLabel = headingMode === 'magnetic' ? 'MAG' : 'TRUE';
    const modeTitle =
        headingMode === 'magnetic'
            ? 'Compass and runway headings are magnetic. Wind sources are always true north.'
            : 'Compass and all headings are true north.';
    const varAbs = Math.abs(magneticVariation);
    const varDir = magneticVariation > 0 ? 'E' : magneticVariation < 0 ? 'W' : '';
    const indicatorTitle = `${varAbs}°${varDir} variation.`;

    // In magnetic mode: straight-up line IS magnetic north → give it the magnetic colour.
    // In true mode: straight-up line IS true north → give it the true colour.
    const straightClass =
        headingMode === 'magnetic'
            ? 'fe-crosswind__variation-line fe-crosswind__variation-line--magnetic'
            : 'fe-crosswind__variation-line fe-crosswind__variation-line--true';
    const rotatedClass =
        headingMode === 'magnetic'
            ? 'fe-crosswind__variation-line fe-crosswind__variation-line--true'
            : 'fe-crosswind__variation-line fe-crosswind__variation-line--magnetic';
    const r = 23;
    const cx = 35;
    const cy = 33;
    const varRad = (otherNorthDeg * Math.PI) / 180;
    const rotX = cx + r * Math.sin(varRad);
    const rotY = cy - r * Math.cos(varRad);
    const largeArc = Math.abs(otherNorthDeg) > 180 ? 1 : 0;
    const sweepArc = otherNorthDeg >= 0 ? 1 : 0;
    const arcFillPath = `M ${cx},${cy - r} A ${r},${r} 0 ${largeArc},${sweepArc} ${rotX},${rotY} L ${cx},${cy} Z`;

    return (
        <div className="fe-crosswind__compass-wrap">
            <canvas
                ref={canvasRef}
                className="fe-crosswind__canvas"
                onMouseMove={handleMove}
                onMouseLeave={() => setHoveredRunwayId(null)}
                onClick={handleClick}
            />
            <span className="fe-crosswind__north-chip" title={modeTitle}>
                {modeLabel}
            </span>
            {showMagVarIndicator && magneticVariation !== 0 && (
                <div className="fe-crosswind__variation-indicator" title={indicatorTitle}>
                    <svg viewBox="0 0 70 36" aria-hidden="true">
                        <path
                            d={arcFillPath}
                            fill={headingMode === 'magnetic' ? 'var(--cw-primary)' : 'var(--cw-text-muted)'}
                            opacity="0.15"
                        />
                        <line x1={cx} y1={cy} x2={cx} y2={cy - r} className={straightClass} />
                        <line x1={cx} y1={cy} x2={rotX} y2={rotY} className={rotatedClass} />
                    </svg>
                </div>
            )}
        </div>
    );
}

export function AirportSection() {
    const { icao, iata, magneticVariation, onAirportSearch, airportSuggestions, onAirportSelect } = useCrosswind();
    const varDir = magneticVariation > 0 ? 'East' : magneticVariation < 0 ? 'West' : 'None';
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const debounceRef = useRef<number | null>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const suppressSearchRef = useRef(false);
    const hasSearch = !!(onAirportSearch || onAirportSelect);

    useEffect(() => {
        if (!hasSearch) {
            return;
        }
        if (suppressSearchRef.current) {
            suppressSearchRef.current = false;
            return;
        }
        if (debounceRef.current) {
            self.clearTimeout(debounceRef.current);
        }
        if (!query.trim()) {
            setOpen(false);
            return;
        }
        debounceRef.current = self.setTimeout(() => {
            onAirportSearch?.(query.trim());
            setOpen(true);
        }, 300);
        return () => {
            if (debounceRef.current) {
                self.clearTimeout(debounceRef.current);
            }
        };
    }, [query, hasSearch, onAirportSearch]);

    useEffect(() => {
        if (airportSuggestions?.length && !suppressSearchRef.current) {
            setOpen(true);
        }
    }, [airportSuggestions]);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    return (
        <div className="fe-cw-card">
            <div className="fe-cw-section-title">Airport</div>

            {hasSearch && (
                <div className="fe-cw-airport-search" ref={wrapperRef}>
                    <input
                        className="fe-cw-input fe-cw-airport-search__input"
                        type="text"
                        placeholder="Search airport by ID"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onFocus={() => {
                            if (airportSuggestions?.length) {
                                setOpen(true);
                            }
                        }}
                        autoComplete="off"
                        spellCheck={false}
                    />
                    {open && airportSuggestions && airportSuggestions.length > 0 && (
                        <ul className="fe-cw-airport-suggestions">
                            {airportSuggestions.map((s) => (
                                <li
                                    key={s.icao}
                                    className="fe-cw-airport-suggestion"
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        suppressSearchRef.current = true;
                                        onAirportSelect?.(s);
                                        setQuery(s.icao);
                                        setOpen(false);
                                    }}>
                                    <span className="fe-cw-airport-suggestion__icao">{s.icao}</span>
                                    {s.iata && <span className="fe-cw-airport-suggestion__iata">{s.iata}</span>}
                                    {s.name && <span className="fe-cw-airport-suggestion__name">{s.name}</span>}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}

            {(icao || iata) && (
                <div className="fe-cw-airport-ids">
                    {icao && <span className="fe-cw-airport-id fe-cw-airport-id--icao">{icao}</span>}
                    {iata && <span className="">/</span>}
                    {iata && <span className="fe-cw-airport-id fe-cw-airport-id--iata">{iata}</span>}
                </div>
            )}
            {magneticVariation !== 0 && (
                <div className={clsx('fe-cw-airport-variation', (icao || iata) && 'fe-cw-airport-variation-border')}>
                    <span className="fe-cw-label">Magnetic variation</span>
                    <span className="fe-cw-airport-variation__value">
                        {Math.abs(magneticVariation)}° {varDir}
                    </span>
                </div>
            )}
        </div>
    );
}

export function RunwaySection() {
    const {
        heading,
        setHeading,
        flipped,
        setFlipped,
        primaryLabel,
        reciprocalLabel,
        runways,
        selectedRunwayPair,
        setSelectedRunwayPair,
        setItwsRunwayId
    } = useCrosswind();
    const [inputValue, setInputValue] = useState(String(heading));
    const focused = useState(false);
    const isFocused = focused[0];
    const setIsFocused = focused[1];

    useEffect(() => {
        if (!isFocused) {
            setInputValue(String(heading));
        }
    }, [heading, isFocused]);

    const wrapHeading = (val: number) => Math.round((((val % 360) + 360) % 360) * 100) / 100;

    const deselect = () => {
        setSelectedRunwayPair(null);
        setItwsRunwayId(null);
    };

    const commitValue = (raw: string) => {
        const val = parseFloat(raw);
        if (!isNaN(val)) {
            const clamped = wrapHeading(val);
            setHeading(clamped);
            setInputValue(String(clamped));
        } else {
            setInputValue(String(heading));
        }
    };

    const baseLabel = selectedRunwayPair ? selectedRunwayPair.base.id : primaryLabel;
    const recipLabel = selectedRunwayPair ? selectedRunwayPair.recip.id : reciprocalLabel;

    return (
        <div className="fe-cw-card">
            <label className="fe-cw-section-title" htmlFor="cw-runway-hdg">
                Runway
            </label>
            {runways && runways.length > 0 && (
                <div className="fe-cw-field" style={{ width: '8em' }}>
                    <select
                        className="fe-cw-select"
                        value={selectedRunwayPair ? Math.max(0, runways.findIndex(p => p.base.id === selectedRunwayPair.base.id)) : 0}
                        onChange={(e) => {
                            const pair = runways[Number(e.target.value)];
                            if (pair) {
                                // If the pair declares a preferred active end, honour it;
                                // otherwise keep the current flip state.
                                const useRecip =
                                    pair.activeEnd !== undefined ? pair.activeEnd === 'recip' : flipped;
                                const newHeading = useRecip ? pair.recip.heading : pair.base.heading;
                                setSelectedRunwayPair(pair);
                                setFlipped(useRecip);
                                setHeading(newHeading);
                                setInputValue(String(newHeading));
                                setItwsRunwayId(null);
                            }
                        }}>
                        {runways.map((pair, i) => (
                            <option key={i} value={i}>
                                {pair.base.id} / {pair.recip.id}
                            </option>
                        ))}
                    </select>
                </div>
            )}
            <div className="fe-cw-runway-row">
                <div className="fe-cw-input-deg-wrap">
                    <input
                        id="cw-runway-hdg"
                        type="number"
                        className="fe-cw-input fe-cw-input--deg"
                        value={inputValue}
                        step={1}
                        onFocus={() => setIsFocused(true)}
                        onChange={(e) => {
                            const raw = e.target.value;
                            setInputValue(raw);
                            deselect();
                            const val = parseFloat(raw);
                            if (!isNaN(val)) {
                                setHeading(wrapHeading(val));
                            }
                        }}
                        onBlur={(e) => {
                            setIsFocused(false);
                            commitValue(e.target.value);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                commitValue((e.target as HTMLInputElement).value);
                            }
                        }}
                    />
                    <span className="fe-cw-unit">°</span>
                </div>
                <SegmentedControl
                    options={[
                        { value: 'primary', label: baseLabel },
                        { value: 'reciprocal', label: recipLabel }
                    ]}
                    value={flipped ? 'reciprocal' : 'primary'}
                    onChange={(v) => {
                        const wantsReciprocal = v === 'reciprocal';
                        if (wantsReciprocal !== flipped) {
                            setFlipped(wantsReciprocal);
                            setItwsRunwayId(null);
                            if (selectedRunwayPair) {
                                const newHdg = wantsReciprocal ? selectedRunwayPair.recip.heading : selectedRunwayPair.base.heading;
                                setHeading(newHdg);
                                setInputValue(String(newHdg));
                            } else {
                                setHeading((heading + 180) % 360);
                            }
                        }
                    }}
                />
            </div>
        </div>
    );
}

export function ControlsSection() {
    const {
        availableSources,
        selectedSource,
        setSelectedSource,
        tafHourlySlots,
        selectedTafHourIndex,
        setSelectedTafHourIndex,
        selectedTafIndex,
        setSelectedTafIndex,
        windSources,
        activeWind,
        gustFactor,
        setGustFactor,
        userWind,
        setUserWind,
        itwsSubSource,
        setItwsSubSource,
        itwsRunwayOptions,
        setItwsRunwayId,
        itwsEffectiveRunwayId,
        itwsFlightType,
        setItwsFlightType,
        selectedRunwayPair,
        flipped,
        headingMode
    } = useCrosswind();

    const activeEnd = selectedRunwayPair ? (flipped ? selectedRunwayPair.recip.id : selectedRunwayPair.base.id) : null;
    const itwsRwyData = windSources?.itws?.runways?.find((r) => r.runwayId === itwsEffectiveRunwayId);
    const hasArrival = !!itwsRwyData?.arrival;
    const hasDeparture = !!itwsRwyData?.departure;
    const itwsMismatch = !!(activeEnd && itwsRunwayOptions.length > 0 && !itwsRunwayOptions.includes(activeEnd));

    useEffect(() => {
        if (hasArrival && !hasDeparture) {
            setItwsFlightType('arrival');
        } else if (!hasArrival && hasDeparture) {
            setItwsFlightType('departure');
        }
    }, [itwsEffectiveRunwayId, hasArrival, hasDeparture]);

    return (
        <div className="fe-cw-card fe-cw-card--tabbed">
            <div className="fe-cw-section-title">Source</div>
            <Tabs
                options={availableSources.map((src) => ({
                    value: src,
                    label: SOURCE_LABELS[src]
                }))}
                value={selectedSource}
                onChange={setSelectedSource}
            />

            <div className="fe-cw-card__body">
                {selectedSource === 'itws' && windSources?.itws && (
                    <>
                        {itwsRunwayOptions.length > 0 && windSources.itws.centerfield && (
                            <div className="fe-cw-field">
                                <span className="fe-cw-label">Data source</span>
                                <SegmentedControl
                                    options={[
                                        { value: 'runway', label: 'Runway' },
                                        { value: 'centerfield', label: 'Centerfield' }
                                    ]}
                                    value={itwsSubSource}
                                    onChange={setItwsSubSource}
                                />
                            </div>
                        )}
                        {itwsSubSource === 'runway' && itwsRunwayOptions.length > 0 && (
                            <>
                                <div className="fe-cw-runway-row">
                                    <select
                                        id="cw-itws-rwy"
                                        className="fe-cw-select fe-cw-select--compact"
                                        value={itwsEffectiveRunwayId ?? ''}
                                        onChange={(e) => setItwsRunwayId(e.target.value || null)}>
                                        {itwsRunwayOptions.map((id) => (
                                            <option key={id} value={id}>
                                                {id}
                                            </option>
                                        ))}
                                    </select>
                                    <SegmentedControl
                                        options={[
                                            { value: 'arrival', label: 'Arrival' },
                                            { value: 'departure', label: 'Departure' }
                                        ]}
                                        title={!hasArrival || !hasDeparture ? 'Only one type available for this runway' : ''}
                                        value={itwsFlightType}
                                        onChange={setItwsFlightType}
                                        disabled={!hasArrival || !hasDeparture}
                                    />
                                </div>
                                {itwsMismatch && (
                                    <div className="fe-cw-itws-warning" style={{ maxWidth: '30em' }}>
                                        No ITWS data for <strong>{activeEnd}</strong> — showing <strong>{itwsEffectiveRunwayId}</strong>
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}

                {selectedSource === 'taf' && windSources?.taf?.periods && (
                    <div className="fe-cw-field">
                        {tafHourlySlots ? (
                            <>
                                <label className="fe-cw-label" htmlFor="cw-taf">
                                    Time (UTC)
                                </label>
                                <select
                                    id="cw-taf"
                                    className="fe-cw-select fe-cw-select--mono"
                                    style={{ maxWidth: '25em' }}
                                    value={selectedTafHourIndex}
                                    onChange={(e) => setSelectedTafHourIndex(Number(e.target.value))}>
                                    {tafHourlySlots.map((s, i) => (
                                        <option key={i} value={i}>
                                            {s.label}
                                        </option>
                                    ))}
                                </select>
                            </>
                        ) : (
                            <>
                                <label className="fe-cw-label" htmlFor="cw-taf">
                                    TAF Period
                                </label>
                                <select
                                    id="cw-taf"
                                    className="fe-cw-select"
                                    style={{ maxWidth: '25em' }}
                                    value={selectedTafIndex}
                                    onChange={(e) => setSelectedTafIndex(Number(e.target.value))}>
                                    {windSources.taf.periods.map((p, i) => (
                                        <option key={i} value={i}>
                                            {p.label}
                                        </option>
                                    ))}
                                </select>
                            </>
                        )}
                    </div>
                )}

                {selectedSource === 'user' && (
                    <div className="fe-crosswind__user-inputs">
                        <div className="fe-cw-field" >
                            <label className="fe-cw-label" htmlFor="cw-dir">
                                Direction (°)
                                {headingMode === 'magnetic' && (
                                    <span className="fe-cw-true-badge" title="Wind direction is always true north">
                                        TRUE
                                    </span>
                                )}
                            </label>
                            <div className="fe-cw-input-deg-wrap">
                                <input
                                    id="cw-dir"
                                    type="number"
                                    className="fe-cw-input fe-cw-input--deg"
                                    value={userWind.direction}
                                    min={0}
                                    max={359}
                                    step={1}
                                    onChange={(e) => setUserWind((p) => ({ ...p, direction: e.target.valueAsNumber || 0 }))}
                                />
                                <span className="fe-cw-unit">°</span>
                            </div>
                        </div>
                        <div className="fe-cw-field">
                            <label className="fe-cw-label" htmlFor="cw-speed">
                                Speed (kt)
                            </label>
                            <input
                                id="cw-speed"
                                type="number"
                                className="fe-cw-input"
                                value={userWind.speed}
                                min={0}
                                step={1}
                                onChange={(e) => setUserWind((p) => ({ ...p, speed: e.target.valueAsNumber || 0 }))}
                            />
                        </div>
                        <div className="fe-cw-field">
                            <label className="fe-cw-label" htmlFor="cw-gust">
                                Gust (kt)
                            </label>
                            <input
                                id="cw-gust"
                                type="number"
                                className="fe-cw-input"
                                value={userWind.gust ?? ''}
                                min={0}
                                step={1}
                                onChange={(e) =>
                                    setUserWind((p) => ({ ...p, gust: e.target.value === '' ? undefined : e.target.valueAsNumber }))
                                }
                            />
                        </div>
                    </div>
                )}

                <div className="fe-cw-field">
                    <span className="fe-cw-label">Gust Factor</span>
                    <SegmentedControl
                        options={[
                            { value: 1, label: '100%' },
                            { value: 0.5, label: '50%' }
                        ]}
                        value={gustFactor}
                        onChange={setGustFactor}
                    />
                </div>

                <div className="fe-crosswind__wind-info" style={{ marginTop: '0.5em' }}>
                    {(() => {
                        return (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.2em' }}>
                                Wind {activeWind.direction}° / {activeWind.speed} kt{activeWind.gust != null && ` G${activeWind.gust}`}
                                <span className="fe-cw-true-badge" title="Wind direction is always true north">
                                    TRUE
                                </span>
                            </div>
                        );
                    })()}
                </div>
            </div>
        </div>
    );
}

export function ResultsSection() {
    const { result } = useCrosswind();
    const hwColor = result.headwind >= 0 ? 'var(--cw-color-headwind)' : 'var(--cw-color-tailwind)';
    return (
        <div className="fe-cw-card">
            <div className="fe-cw-section-title">Wind components</div>
            <div className="fe-crosswind__results">
                <div className="fe-crosswind__result-item" style={{ borderLeftColor: hwColor }}>
                    <span className="fe-crosswind__result-label">{result.headwind >= 0 ? 'Headwind' : 'Tailwind'}</span>
                    <span className="fe-crosswind__result-value">{Math.abs(result.headwind)} kt</span>
                </div>
                <div className="fe-crosswind__result-item" style={{ borderLeftColor: 'var(--cw-color-crosswind)' }}>
                    <span className="fe-crosswind__result-label">Crosswind {result.crosswind >= 0 ? '(Right)' : '(Left)'}</span>
                    <span className="fe-crosswind__result-value">{Math.abs(result.crosswind)} kt</span>
                </div>
            </div>
        </div>
    );
}

function DivergingBar({
    value,
    scale,
    color,
    limit,
    limitSides = 'both'
}: {
    value: number;
    scale: number;
    color: string;
    limit?: number;
    limitSides?: 'both' | 'negative';
}) {
    const half = Math.min(Math.abs(value) / scale, 1) * 50;
    const limitPct = limit != null ? Math.min(limit / scale, 1) * 50 : null;
    return (
        <span className="fe-cw-bar" aria-hidden="true">
            <span className="fe-cw-bar__fill" style={{ left: `${value >= 0 ? 50 : 50 - half}%`, width: `${half}%`, background: color }} />
            {limitPct != null && (
                <>
                    {limitSides === 'both' && <span className="fe-cw-bar__limit" style={{ left: `${50 + limitPct}%` }} />}
                    <span className="fe-cw-bar__limit" style={{ left: `${50 - limitPct}%` }} />
                </>
            )}
        </span>
    );
}

const USAGE_LABELS: Record<RunwayUsage, { text: string; title: string }> = {
    arrival: { text: 'ARR', title: 'Used for arrivals' },
    departure: { text: 'DEP', title: 'Used for departures' },
    both: { text: 'D/A', title: 'Used for departures and arrivals' }
};

export function RunwayTableSection() {
    const {
        runwayRows,
        runwayRanking,
        showBestRunway,
        showRunwayBars,
        showRunwayUsage,
        selectedRunwayPair,
        flipped,
        hoveredRunwayId,
        setHoveredRunwayId,
        selectRunway
    } = useCrosswind();

    const { maxCrosswind, maxTailwind } = runwayRanking ?? {};
    const scale = useMemo(() => runwayBarScale(runwayRows, runwayRanking), [runwayRows, runwayRanking]);
    const usageColumn = showRunwayUsage && hasRunwayUsage(runwayRows);

    if (!runwayRows.length) {
        return null;
    }

    const activeId = selectedRunwayPair ? (flipped ? selectedRunwayPair.recip.id : selectedRunwayPair.base.id) : null;

    return (
        <div className="fe-cw-card fe-cw-runway-table-card">
            <div className="fe-cw-section-title">Runways table</div>
            <table className="fe-cw-runway-table">
                <thead>
                    <tr>
                        <th className="fe-cw-runway-table__th fe-cw-runway-table__th--rwy">RWY</th>
                        {usageColumn && <th className="fe-cw-runway-table__th fe-cw-runway-table__th--use">Use</th>}
                        <th className="fe-cw-runway-table__th fe-cw-runway-table__th--val">TW / HW</th>
                        <th className="fe-cw-runway-table__th fe-cw-runway-table__th--val">XW</th>
                    </tr>
                </thead>
                <tbody>
                    {runwayRows.map((row) => {
                        const hw = row.result.headwind;
                        const xw = row.result.crosswind;
                        const isActive = row.id === activeId;
                        const isBest = showBestRunway && row.isBest;
                        const exceeded = row.status === 'exceeded';
                        const hwBarColor = hw >= 0 ? 'var(--cw-color-headwind)' : 'var(--cw-color-tailwind)';
                        const hwTextColor = hw >= 0 ? 'var(--cw-text-headwind)' : 'var(--cw-text-tailwind)';
                        return (
                            <tr
                                key={row.id}
                                className={clsx(
                                    'fe-cw-runway-table__row',
                                    'fe-cw-runway-table__row--clickable',
                                    isActive && 'fe-cw-runway-table__row--active',
                                    isBest && 'fe-cw-runway-table__row--best',
                                    exceeded && 'fe-cw-runway-table__row--exceeded',
                                    hoveredRunwayId === row.id && 'fe-cw-runway-table__row--hovered'
                                )}
                                onMouseEnter={() => setHoveredRunwayId(row.id)}
                                onMouseLeave={() => setHoveredRunwayId(null)}
                                onClick={() => selectRunway(row)}>
                                <td className="fe-cw-runway-table__id">
                                    <span className="fe-cw-runway-table__id-wrap">
                                        {row.id}
                                        {isBest && (
                                            <span
                                                className="fe-cw-runway-table__badge"
                                                title="Highest score for the current wind. Advisory only - not an operational recommendation.">
                                                BEST
                                            </span>
                                        )}
                                        {exceeded && (
                                            <span
                                                className="fe-cw-runway-table__badge fe-cw-runway-table__badge--warn"
                                                title="Exceeds a configured wind limit">
                                                !
                                            </span>
                                        )}
                                    </span>
                                </td>
                                {usageColumn && (
                                    <td className="fe-cw-runway-table__use">
                                        {row.usage && (
                                            <span
                                                className={`fe-cw-usage fe-cw-usage--${row.usage}`}
                                                title={USAGE_LABELS[row.usage].title}>
                                                {USAGE_LABELS[row.usage].text}
                                            </span>
                                        )}
                                    </td>
                                )}
                                <td className="fe-cw-runway-table__val">
                                    {showRunwayBars && (
                                        <DivergingBar
                                            value={row.raw.headwind}
                                            scale={scale}
                                            color={hwBarColor}
                                            limit={maxTailwind}
                                            limitSides="negative"
                                        />
                                    )}
                                    <span className="fe-cw-runway-table__num" style={{ color: hwTextColor }}>
                                        {hw >= 0 ? '+' : '−'}
                                        {Math.abs(hw)}
                                    </span>
                                </td>
                                <td className="fe-cw-runway-table__val">
                                    {showRunwayBars && (
                                        <DivergingBar
                                            value={row.raw.crosswind}
                                            scale={scale}
                                            color="var(--cw-color-crosswind)"
                                            limit={maxCrosswind}
                                        />
                                    )}
                                    <span className="fe-cw-runway-table__num" style={{ color: 'var(--cw-text-crosswind)' }}>
                                        {Math.abs(xw)} {xw >= 0 ? 'R' : 'L'}
                                    </span>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
